/**
 * Shield-with-heart mark for the travel assistance, drawn rather than shipped as an image.
 *
 * Brasilia ships the same shape as a 62x70 PNG with its blue baked into the pixels, so its
 * colour cannot follow a theme. Drawing it makes the colour a parameter: a brand that
 * changes its palette changes the mark with no new asset. That matters here because Rápido
 * Ochoa competes with Brasilia directly — the shape is shared insurance iconography, the
 * colour is what separates the two.
 *
 * It ships as a data URI because that is the only way to theme it without editing a shared
 * component: `DialogMessage` declares `iconType: PropTypes.string`, so it takes an icon name
 * or an image URL and never a React node. The design system already serves `badgeQuestion`
 * this way.
 */

/** Used when a tenant's theme carries no primary colour, so the mark still renders. */
const FALLBACK_COLOR = '#4A4A4A';

/**
 * Shield outline: a straight peak at the top, vertical flanks, tapering to a point at the
 * bottom. Fitted against Brasilia's PNG at 99.2% silhouette overlap.
 *
 * The top is two straight lines meeting in a 1px flat, not a curve. Measured on the
 * original, the left edge runs 30, 25, 20, 16, 11, 6, 2, 0 over the first fourteen rows —
 * a line, not an arc. An earlier revision used a cubic there and read as a dome.
 */
const SHIELD = 'M0 14 L30 0 L31 0 L61 14 L61 32 C61 48 48 59 30.5 70 C13 59 0 48 0 32 Z';

/** Heart, at 98.5% overlap with the original. White, as Brasilia's is. */
const HEART =
  'M29.5 49 C22 42 14 35 14 28 C14 23.5 17.5 21 21.5 21 C25 21 28 23 29.5 25.5 ' +
  'C31 23 34 21 37.5 21 C41.5 21 45 23.5 45 28 C45 35 37 42 29.5 49 Z';

/**
 * Build the insurance shield as a data URI, tinted with the colour given.
 *
 * The `#` of a hex colour is percent-encoded: left raw, a browser reads it as the start of a
 * fragment and drops the rest of the markup, which renders a broken image rather than
 * failing loudly.
 *
 * @param {string} color - Fill for the shield, usually the theme's `colors.primary`.
 * @returns {string} - A `data:image/svg+xml` URI.
 */
const insuranceShieldIcon = (color) => {
  const fill = (color || FALLBACK_COLOR).replace('#', '%23');

  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 62 70'>` +
    `<path fill='${fill}' d='${SHIELD}'/>` +
    `<path fill='%23FFFFFF' d='${HEART}'/>` +
    `</svg>`;

  return `data:image/svg+xml,${svg.replace(/</g, '%3C').replace(/>/g, '%3E')}`;
};

export default insuranceShieldIcon;
